import { Component, type ReactNode, useState } from 'react';
import ReactJsonView from '@microlink/react-json-view';
import { ActionIcon, Box, Code, Group, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { HiCheck } from 'react-icons/hi';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';

type Props = {
  src: unknown;
  height?: string;
};

const toViewerValue = (value: unknown) => {
  if (value !== null && typeof value === 'object') return value as object;
  return { value };
};

const stringify = (value: unknown): string => {
  const seen = new WeakSet<object>();

  try {
    const json = JSON.stringify(
      value,
      (_key, nestedValue) => {
        if (typeof nestedValue === 'bigint') {
          return `${nestedValue.toString()}n`;
        }

        if (nestedValue !== null && typeof nestedValue === 'object') {
          if (seen.has(nestedValue)) {
            return '[Circular]';
          }
          seen.add(nestedValue);
        }

        return nestedValue;
      },
      2,
    );
    return json ?? String(value);
  } catch {
    return String(value);
  }
};

const parseViewerValue = (json: string) => {
  try {
    return toViewerValue(JSON.parse(json) as unknown);
  } catch {
    return { value: json };
  }
};

class JsonViewerErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode; resetKey: string },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Readonly<{ children: ReactNode; fallback: ReactNode; resetKey: string }>) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) return this.props.fallback;

    return this.props.children;
  }
}

const MetadataJsonViewer = ({ src, height = 'calc(100vh - 158px)' }: Props) => {
  const [copied, setCopied] = useState(false);
  const json = stringify(src);
  const viewerValue = parseViewerValue(json);

  const copyMetadata = async () => {
    await navigator.clipboard.writeText(json);
    notifications.show({
      message: 'Metadata copied to clipboard',
      color: 'blue',
    });
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ScrollArea h={height} type="auto" scrollbars="y" offsetScrollbars="y">
      <Box
        bg="white"
        px="lg"
        pt="sm"
        pb="4rem"
        style={{
          minHeight: '100%',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <Group
          justify="flex-end"
          mb="xs"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: 'white',
            paddingBottom: '8px',
          }}
        >
          <Tooltip label={copied ? 'Copied' : 'Copy metadata JSON'} openDelay={250}>
            <ActionIcon
              aria-label="Copy metadata JSON"
              variant="subtle"
              color={copied ? 'green' : 'gray'}
              size="sm"
              disabled={copied}
              onClick={copyMetadata}
            >
              <AppIcon Icon={copied ? HiCheck : HiOutlineDocumentDuplicate} size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <JsonViewerErrorBoundary
          resetKey={json}
          fallback={
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                The interactive JSON tree could not render this payload. Showing raw JSON instead.
              </Text>
              <Code block data-testid="metadata-json-fallback">
                {json}
              </Code>
            </Stack>
          }
        >
          <ReactJsonView
            src={viewerValue}
            name={false}
            quotesOnKeys={false}
            escapeStrings={false}
            collapsed={2}
            displayDataTypes={false}
            iconStyle="triangle"
            collapseStringsAfterLength={120}
            enableClipboard={true}
          />
        </JsonViewerErrorBoundary>
      </Box>
    </ScrollArea>
  );
};

export default MetadataJsonViewer;
